import React, { ReactNode } from "react";
import { CheckCircleIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import BrandLogo from "../brand/BrandLogo";
import LanguageSelector from "../language/LanguageSelector";
import { useTranslation } from "../../i18n/LanguageContext";

type AuthShellProps = {
  eyebrow?: string;
  title: string;
  description: string;
  children: ReactNode;
  backHref?: string;
  backLabel?: string;
};

export default function AuthShell({
  eyebrow,
  title,
  description,
  children,
  backHref,
  backLabel,
}: AuthShellProps) {
  const { t } = useTranslation();

  return (
    <main className="auth-shell">
      <section className="auth-brand-panel" aria-label="Eupago">
        <div className="auth-brand-glow auth-brand-glow-one" />
        <div className="auth-brand-glow auth-brand-glow-two" />

        <div className="relative z-10 flex h-full max-w-xl flex-col justify-between">
          <div className="inline-flex w-fit self-start rounded-2xl bg-white px-5 py-3 shadow-lg ring-1 ring-white/70">
            <BrandLogo darkText partnerLabel />
          </div>

          <div className="py-12">
            <span className="auth-kicker">{t('auth.kicker')}</span>
            <h2 className="mt-6 text-4xl font-black leading-tight text-white lg:text-5xl">
              {t('auth.hero')}
            </h2>
            <p className="mt-5 max-w-lg text-base leading-7 text-white/70">
              {t('auth.heroDescription')}
            </p>

            <div className="mt-9 grid gap-4 sm:grid-cols-2">
              <div className="auth-feature">
                <ShieldCheckIcon className="h-6 w-6 text-eupago-mint" />
                <span>{t('auth.protected')}</span>
              </div>
              <div className="auth-feature">
                <CheckCircleIcon className="h-6 w-6 text-eupago-mint" />
                <span>{t('auth.updated')}</span>
              </div>
            </div>
          </div>

          <p className="text-xs font-medium tracking-wide" style={{ color: "rgba(255, 255, 255, 0.62)" }}>
            ©2014-2026 Eupago
          </p>
        </div>
      </section>

      <section className="auth-form-panel relative">
        <div className="absolute right-5 top-5 z-20 sm:right-8 sm:top-7">
          <LanguageSelector />
        </div>
        <div className="auth-card">
          <BrandLogo darkText partnerLabel className="mx-auto mb-8 lg:hidden" />
          <p className="auth-form-eyebrow">{eyebrow || t('auth.platform')}</p>
          <h1 className="auth-title">{title}</h1>
          <p className="auth-description">{description}</p>
          <div className="mt-8">{children}</div>
          {backHref && backLabel && (
            <Link className="auth-back-link" to={backHref}>
              <span>{backLabel}</span>
            </Link>
          )}
        </div>
      </section>
    </main>
  );
}
